export const STEP_TITLE_ID = 'step-title';

const getFlowStepHeading = (): HTMLElement | null =>
  document.getElementById(STEP_TITLE_ID) ?? document.querySelector<HTMLElement>('main h1');

const focusElement = (target: HTMLElement): void => {
  if (!target.hasAttribute('tabindex')) {
    target.tabIndex = -1;
  }

  target.focus();
};

/**
 * After a flow step change, scroll to the top and move focus to the new heading
 * so screen readers start on the question instead of the skip-to-main link
 * (WCAG 2.4.3). Combined with `--flow-header-height` scroll padding, this keeps
 * the heading below the sticky bar (WCAG 2.4.11).
 *
 * Returns whether a heading was focused. Callers that run during a route
 * transition should use `waitForFlowStepHeadingFocus` so loading spinners
 * (`Layout.Content isLoading`) are not treated as a finished step.
 */
export const focusFlowStepHeading = (): boolean => {
  const heading = getFlowStepHeading();

  if (!heading) return false;

  window.scrollTo(0, 0);
  focusElement(heading);
  return true;
};

/**
 * Focus the step heading as soon as it is in the DOM. Some steps replace the
 * heading with a spinner until data loads; this keeps watching until the
 * heading mounts, then disconnects.
 */
export const waitForFlowStepHeadingFocus = (): (() => void) => {
  if (focusFlowStepHeading()) {
    return () => undefined;
  }

  const root = document.getElementById('main') ?? document.body;
  const observer = new MutationObserver(() => {
    if (focusFlowStepHeading()) {
      observer.disconnect();
    }
  });

  observer.observe(root, { childList: true, subtree: true });

  return () => observer.disconnect();
};
